import { Opportunity, DebriefFactor, WinLossAggregateStats } from "../types";

export const ALL_DEBRIEF_FACTORS: { factor: DebriefFactor; label: string }[] = [
  { factor: "wbe_certification", label: "WBE Certification Advantage" },
  { factor: "technical_solution", label: "Technical Solution & Methodology" },
  { factor: "pricing", label: "Pricing / Cost Proposal" },
  { factor: "past_performance", label: "Past Performance & References" },
  { factor: "incumbent_advantage", label: "Incumbent Advantage" },
  { factor: "compliance_gap", label: "Compliance & Form Gaps" },
  { factor: "turnaround", label: "Turnaround & Delivery Capacity" },
  { factor: "timeline", label: "Timeline Feasibility" },
  { factor: "relationship", label: "Client Relationship & Trust" },
  { factor: "other", label: "Other Factors" },
];

/**
 * Computes aggregate win/loss analytics across decided RFP opportunities.
 */
export function computeWinLossStats(opportunities: Opportunity[]): WinLossAggregateStats {
  const decided = opportunities.filter(
    (o) =>
      o.outcome === "won" ||
      o.outcome === "lost" ||
      o.outcome === "disqualified" ||
      o.outcome === "withdrawn",
  );

  const won = decided.filter((o) => o.outcome === "won");
  const lost = decided.filter((o) => o.outcome === "lost");
  const disqualified = decided.filter((o) => o.outcome === "disqualified");
  const withdrawn = decided.filter((o) => o.outcome === "withdrawn");

  const competitiveTotal = won.length + lost.length;
  const winRate = competitiveTotal > 0 ? Math.round((won.length / competitiveTotal) * 100) : 0;

  // Contract values
  const totalWonValue = won.reduce((sum, o) => {
    const val = o.postMortem?.finalContractValue;
    return typeof val === "number" && !isNaN(val) ? sum + val : sum;
  }, 0);

  const totalLostValue = lost.reduce((sum, o) => {
    const val = o.postMortem?.finalContractValue;
    return typeof val === "number" && !isNaN(val) ? sum + val : sum;
  }, 0);

  // Cycle time calculation: from creation to decision date
  let totalCycleDays = 0;
  let cycleCount = 0;

  decided.forEach((o) => {
    if (o.postMortem?.decidedAt && o.createdAt) {
      const created = new Date(o.createdAt).getTime();
      const decidedTime = new Date(o.postMortem.decidedAt).getTime();
      const diffDays = Math.max(0, Math.round((decidedTime - created) / (1000 * 60 * 60 * 24)));
      totalCycleDays += diffDays;
      cycleCount++;
    }
  });

  const avgCycleTimeDays = cycleCount > 0 ? Math.round(totalCycleDays / cycleCount) : 0;

  // Breakdown by Service
  const serviceMap = new Map<string, { won: number; lost: number; total: number }>();

  decided.forEach((o) => {
    const services =
      Array.isArray(o.services) && o.services.length > 0 ? o.services : ["General Translation"];
    services.forEach((s) => {
      const existing = serviceMap.get(s) || { won: 0, lost: 0, total: 0 };
      existing.total++;
      if (o.outcome === "won") existing.won++;
      if (o.outcome === "lost") existing.lost++;
      serviceMap.set(s, existing);
    });
  });

  const serviceBreakdown = Array.from(serviceMap.entries())
    .map(([service, stats]) => {
      const denom = stats.won + stats.lost;
      const rate = denom > 0 ? Math.round((stats.won / denom) * 100) : 0;
      return {
        service,
        won: stats.won,
        lost: stats.lost,
        total: stats.total,
        winRate: rate,
      };
    })
    .sort((a, b) => b.total - a.total);

  // Factor frequencies
  const factorMap = new Map<DebriefFactor, { wonCount: number; lostCount: number }>();
  ALL_DEBRIEF_FACTORS.forEach((f) => {
    factorMap.set(f.factor, { wonCount: 0, lostCount: 0 });
  });

  decided.forEach((o) => {
    const factors = o.postMortem?.factors || [];
    factors.forEach((f) => {
      const entry = factorMap.get(f) || { wonCount: 0, lostCount: 0 };
      if (o.outcome === "won") entry.wonCount++;
      if (o.outcome === "lost") entry.lostCount++;
      factorMap.set(f, entry);
    });
  });

  const factorFrequency = ALL_DEBRIEF_FACTORS.map((f) => {
    const counts = factorMap.get(f.factor) || { wonCount: 0, lostCount: 0 };
    return {
      factor: f.factor,
      label: f.label,
      wonCount: counts.wonCount,
      lostCount: counts.lostCount,
    };
  }).sort((a, b) => b.wonCount + b.lostCount - (a.wonCount + a.lostCount));

  // Top winning competitors
  const competitorMap = new Map<string, number>();
  lost.forEach((o) => {
    const comp = o.postMortem?.winningCompetitor?.trim();
    if (comp) {
      competitorMap.set(comp, (competitorMap.get(comp) || 0) + 1);
    }
  });

  const topCompetitors = Array.from(competitorMap.entries())
    .map(([name, lossCount]) => ({ name, lossCount }))
    .sort((a, b) => b.lossCount - a.lossCount);

  return {
    totalDecided: decided.length,
    wonCount: won.length,
    lostCount: lost.length,
    disqualifiedCount: disqualified.length,
    withdrawnCount: withdrawn.length,
    winRate,
    totalWonValue,
    totalLostValue,
    avgCycleTimeDays,
    serviceBreakdown,
    factorFrequency,
    topCompetitors,
  };
}
